import { Controller } from "@hotwired/stimulus"
import { seedOf, pattern, sway, judge, RAGGED_MS, HELM_ABOVE, HELM_REACH, SHIELD_REACH } from "../lists/tilt"

// The tilt yard (see Lists). A course is dealt by the server — a rival, a
// seed — and ridden here: three passes, each a charge along the rail. The
// hand aims the lance; the lance sways at the canter (the seed says how) and
// has to be held; the ring over the stands closes on the instant the horses
// meet, and the strike goes when the hand says. What the browser judges is a
// preview for the flash; the strike goes to the server with its moment and
// its aim, and the herald's call is the server's verdict.
//
// Geometry is the yard's viewBox, 720 by 360. The riders are the server's
// sprites drawn 300 units square (the ground at 176 of 200, so 264); you
// ride before the rail, the rival behind it, and the two meet mid-yard with
// your lance's tip on their shield.
const YARD = { w: 720, h: 360 }
const ME = { start: -320, meet: 85, y: 40 }
const RIVAL = { start: 740, meet: 335, y: -24 }
// The rival's figure, in yard units from their group's origin: the crown of
// the helm, the span down to the saddle that `aim` runs over (0 crown, 1
// saddle), and where across the figure the shield is.
const RIVAL_CROWN = 84
const AIM_SPAN = 84
const RIVAL_SHIELD_X = 157
// Where your hand holds the lance, from your group's origin.
const ME_HAND = { x: 177, y: 165 }
const LANCE_LENGTH = 300
// The ring closes on the shield: open wide as the charge begins, down to the
// shield's own ring at the instant the horses meet.
const RING = { goal: SHIELD_REACH * AIM_SPAN, open: 100, closeMs: 900 }
const READY_MS = 900
const AFTER_MS = 700
const CALL_MS = 1900
const DRIFT_RATE = 0.00028
const KEY_RATE = 0.0011

export default class extends Controller {
  static targets = [
    "yard", "veil", "mine", "mineArt", "rival", "rivalArt", "marks", "helmMark", "shieldMark",
    "lance", "tip", "ringGroup", "ring", "ringGoal", "flash", "call", "tally", "button", "left",
    "error", "board", "standing"
  ]
  static values = { rideUrl: String, strikeUrl: String, coursesLeft: Number, passes: Number }

  connect() {
    this.course = null
    this.pass = null
    this.hand = 0.5
    this.keys = new Set()
    this.onPointerMove = this.pointerMove.bind(this)
    this.onPointerDown = this.pointerDown.bind(this)
    this.onPointerUp = this.pointerUp.bind(this)
    this.onKeyDown = this.keyDown.bind(this)
    this.onKeyUp = this.keyUp.bind(this)
    this.yardTarget.addEventListener("pointermove", this.onPointerMove)
    this.yardTarget.addEventListener("pointerdown", this.onPointerDown)
    this.yardTarget.addEventListener("pointerup", this.onPointerUp)
    this.yardTarget.addEventListener("pointercancel", this.onPointerUp)
    this.yardTarget.addEventListener("keydown", this.onKeyDown)
    this.yardTarget.addEventListener("keyup", this.onKeyUp)
    this.loop = this.frame.bind(this)
  }

  disconnect() {
    cancelAnimationFrame(this.frameId)
    clearTimeout(this.callTimer)
    this.yardTarget.removeEventListener("pointermove", this.onPointerMove)
    this.yardTarget.removeEventListener("pointerdown", this.onPointerDown)
    this.yardTarget.removeEventListener("pointerup", this.onPointerUp)
    this.yardTarget.removeEventListener("pointercancel", this.onPointerUp)
    this.yardTarget.removeEventListener("keydown", this.onKeyDown)
    this.yardTarget.removeEventListener("keyup", this.onKeyUp)
  }

  // ----------------------------------------------------------------- course

  async ride() {
    if (this.course || this.busy) return
    this.busy = true
    this.buttonTarget.disabled = true
    this.showError(null)
    try {
      const response = await fetch(this.rideUrlValue, { method: "POST", headers: this.headers() })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(payload.error || "The marshal shook his head.")
      this.begin(payload)
    } catch (error) {
      this.showError(error.message)
      this.buttonTarget.disabled = false
    } finally {
      this.busy = false
    }
  }

  begin(course) {
    this.course = course
    this.seed = seedOf(course.seed)
    this.mine = 0
    this.theirs = 0
    this.rivalArtTarget.innerHTML = course.rival_svg
    if (course.mine_svg) this.mineArtTarget.innerHTML = course.mine_svg
    this.veilTarget.hidden = true
    this.yardTarget.classList.add("ll-joust--live")
    this.yardTarget.focus({ preventScroll: true })
    this.say(`${course.rival.name}, ${course.rival.epithet}. ${this.valueOfPasses() - course.ridden} ${course.ridden ? "passes left" : "passes"}.`)
    this.startPass(course.ridden)
  }

  valueOfPasses() {
    return this.passesValue || 3
  }

  startPass(index) {
    this.pass = {
      index, pattern: pattern(this.seed, index), phase: "ready", t0: performance.now(), t: 0,
      drift: 0, struck: null, verdict: null
    }
    this.hand = 0.5
    this.tally(index)
    this.place(ME.start, RIVAL.start, 0)
    this.marksTarget.setAttribute("visibility", "visible")
    this.lanceTarget.setAttribute("visibility", "visible")
    this.tipTarget.setAttribute("visibility", "visible")
    this.ringGroupTarget.setAttribute("visibility", "visible")
    this.ringTarget.setAttribute("r", RING.open)
    this.helmMarkTarget.setAttribute("r", (HELM_REACH * AIM_SPAN).toFixed(1))
    this.shieldMarkTarget.setAttribute("r", (SHIELD_REACH * AIM_SPAN).toFixed(1))
    this.ringGoalTarget.setAttribute("r", (SHIELD_REACH * AIM_SPAN).toFixed(1))
    this.flashTarget.setAttribute("visibility", "hidden")
    this.helmMarkTarget.classList.remove("ll-joust__mark--hit")
    this.shieldMarkTarget.classList.remove("ll-joust__mark--hit")
    cancelAnimationFrame(this.frameId)
    this.lastFrame = performance.now()
    this.frameId = requestAnimationFrame(this.loop)
  }

  // ------------------------------------------------------------------ frame

  frame(now) {
    const pass = this.pass
    if (!pass) return
    const dt = Math.min(now - this.lastFrame, 50)
    this.lastFrame = now

    if (pass.phase === "ready") {
      if (now - pass.t0 >= READY_MS) {
        pass.phase = "riding"
        pass.t0 = now
        pass.t = 0
      }
      this.draw(0)
    } else if (pass.phase === "riding") {
      pass.t = now - pass.t0
      this.steer(pass, dt)
      this.draw(pass.t)
      if (pass.t > pass.pattern.meetMs + RAGGED_MS + 40) {
        this.strike(pass.t)
        return
      }
    } else if (pass.phase === "struck") {
      // Frozen on the strike while the herald is asked; the horses carry on
      // a little past each other once the word is in.
      const since = now - pass.struckAt
      this.draw(pass.struck.ms + Math.min(since, AFTER_MS) * 0.6, true)
    }

    this.frameId = requestAnimationFrame(this.loop)
  }

  steer(pass, dt) {
    const p = pass.pattern
    const noise = (Math.sin((pass.t * p.w1 * 2.7) + p.p2) * 0.6) + (Math.sin((pass.t * p.w2 * 1.9) + p.p1) * 0.4)
    pass.drift += noise * DRIFT_RATE * p.drift * dt
    if (this.keys.has("up")) this.hand -= KEY_RATE * dt
    if (this.keys.has("down")) this.hand += KEY_RATE * dt
    this.hand = Math.min(Math.max(this.hand, 0), 1)
  }

  aim() {
    return Math.min(Math.max(this.hand + (this.pass?.drift || 0), 0), 1)
  }

  draw(t, frozen = false) {
    const pass = this.pass
    const p = pass.pattern
    const k = t / p.meetMs
    const meX = ME.start + ((ME.meet - ME.start) * k)
    const rivalX = RIVAL.start + ((RIVAL.meet - RIVAL.start) * k)
    this.place(meX, rivalX, t)

    const shield = sway(p, Math.min(t, p.meetMs))
    const shieldY = RIVAL.y + RIVAL_CROWN + (shield * AIM_SPAN)
    const helmY = RIVAL.y + RIVAL_CROWN + ((shield - HELM_ABOVE) * AIM_SPAN)
    const marksX = rivalX + RIVAL_SHIELD_X
    this.helmMarkTarget.setAttribute("cx", marksX)
    this.helmMarkTarget.setAttribute("cy", helmY)
    this.shieldMarkTarget.setAttribute("cx", marksX)
    this.shieldMarkTarget.setAttribute("cy", shieldY)

    const aim = frozen ? pass.struck.aim : this.aim()
    const tipX = marksX - 10
    const tipY = RIVAL.y + RIVAL_CROWN + (aim * AIM_SPAN)
    this.tipTarget.setAttribute("transform", `translate(${tipX.toFixed(1)} ${tipY.toFixed(1)})`)
    // The lance is couched at the hand and points at the tip; it is as long
    // as a lance, so it only reaches the rival as the horses meet.
    const handX = meX + ME_HAND.x
    const handY = ME.y + ME_HAND.y
    const reach = Math.hypot(tipX - handX, tipY - handY)
    const along = Math.min(1, LANCE_LENGTH / Math.max(reach, 1))
    this.lanceTarget.setAttribute("x1", handX.toFixed(1))
    this.lanceTarget.setAttribute("y1", handY.toFixed(1))
    this.lanceTarget.setAttribute("x2", (handX + ((tipX - handX) * along)).toFixed(1))
    this.lanceTarget.setAttribute("y2", (handY + ((tipY - handY) * along)).toFixed(1))

    const left = p.meetMs - Math.min(t, p.meetMs + RAGGED_MS)
    const r = left >= 0 ? RING.goal + ((RING.open - RING.goal) * Math.min(1, left / RING.closeMs)) : Math.max(4, RING.goal - ((-left / RAGGED_MS) * 10))
    for (const ring of [ this.ringTarget, this.ringGoalTarget ]) {
      ring.setAttribute("cx", marksX)
      ring.setAttribute("cy", shieldY)
    }
    this.ringTarget.setAttribute("r", r.toFixed(1))
    this.ringTarget.classList.toggle("ll-joust__ring--near", Math.abs(left) <= 170)
  }

  place(meX, rivalX, t) {
    const bob = t ? Math.sin(t / 95) * 2.2 : 0
    this.mineTarget.setAttribute("transform", `translate(${meX.toFixed(1)} ${(ME.y + bob).toFixed(1)})`)
    this.rivalTarget.setAttribute("transform", `translate(${rivalX.toFixed(1)} ${(RIVAL.y - bob).toFixed(1)})`)
  }

  // ----------------------------------------------------------------- strike

  async strike(atMs = null) {
    const pass = this.pass
    if (!pass || pass.phase !== "riding") return
    const ms = atMs == null ? pass.t : atMs
    const aim = this.aim()
    pass.struck = { ms, aim }
    pass.struckAt = performance.now()
    pass.phase = "struck"

    const preview = judge(pass.pattern, ms, aim)
    if (preview.zone === "helm") this.helmMarkTarget.classList.add("ll-joust__mark--hit")
    if (preview.zone === "shield") this.shieldMarkTarget.classList.add("ll-joust__mark--hit")
    this.flash(preview.zone === "miss" ? (preview.timing === "wide" ? "wide" : "miss") : preview.broke ? "a lance broken" : preview.zone)

    try {
      const response = await fetch(this.strikeUrlValue.replace("COURSE", this.course.id), {
        method: "POST", headers: this.headers(),
        body: JSON.stringify({ strike_ms: Math.round(ms), aim: Number(aim.toFixed(4)) })
      })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(payload.error || "The herald lost his voice.")
      this.judged(payload)
    } catch (error) {
      this.showError(error.message)
      this.finishCourse(null)
    }
  }

  judged(payload) {
    const pass = payload.pass
    this.mine += pass.points
    this.theirs += pass.rival
    this.say(payload.call)
    this.tally(this.pass.index + 1)
    if (payload.standing?.board_html && this.hasBoardTarget) {
      this.boardTarget.outerHTML = payload.standing.board_html
    }

    // Held so a page left between the call and the next pass does not start
    // a pass — and a frame loop — on a yard that is no longer there.
    clearTimeout(this.callTimer)
    if (payload.points == null) {
      this.callTimer = setTimeout(() => this.startPass(this.pass.index + 1), CALL_MS)
    } else {
      this.callTimer = setTimeout(() => this.finishCourse(payload), CALL_MS)
    }
  }

  finishCourse(payload) {
    cancelAnimationFrame(this.frameId)
    this.pass = null
    this.course = null
    this.yardTarget.classList.remove("ll-joust--live")
    for (const target of [ this.marksTarget, this.lanceTarget, this.tipTarget, this.ringGroupTarget, this.flashTarget ]) {
      target.setAttribute("visibility", "hidden")
    }
    if (!payload) {
      this.buttonTarget.disabled = false
      return
    }

    const standing = payload.standing || {}
    this.coursesLeftValue = payload.courses_left
    const left = payload.courses_left
    const how = payload.won ? `The course is yours: ${payload.points} points.` : `The course goes to ${payload.rival.name}; ${payload.points} points to you for it.`
    const stand = standing.place ? ` You stand ${this.ordinal(standing.place)} with ${standing.points} points.` : ""
    const more = left > 0 ? ` ${left} ${left === 1 ? "course" : "courses"} left today.` : " Your courses are ridden; the board settles at midnight."
    this.say(how + stand + more)
    if (this.hasStandingTarget) this.standingTarget.textContent = (stand + more).trim()
    this.leftTarget.textContent = left > 0 ? `${left} ${left === 1 ? "course" : "courses"} left today` : "No courses left today"
    this.buttonTarget.textContent = left > 0 ? "Ride again" : "Ridden for today"
    this.buttonTarget.disabled = left <= 0
    this.veilTarget.hidden = false
    this.veilTarget.querySelector("p").textContent = left > 0
      ? "Move to aim (or ↑ ↓). Tap, click or press space to strike as the ring closes."
      : "All three courses ridden. Come back after midnight."
  }

  // ------------------------------------------------------------------ hands

  pointerMove(event) {
    if (!this.pass) return
    if (event.pointerType === "touch" && !this.touching) return
    this.hand = this.handFrom(event)
  }

  pointerDown(event) {
    if (!this.pass) return
    event.preventDefault()
    if (event.pointerType === "touch") {
      this.touching = true
      this.hand = this.handFrom(event)
      this.yardTarget.setPointerCapture?.(event.pointerId)
    } else {
      this.hand = this.handFrom(event)
      this.strike()
    }
  }

  pointerUp(event) {
    if (event.pointerType !== "touch" || !this.touching) return
    this.touching = false
    if (this.pass) {
      this.hand = this.handFrom(event)
      this.strike()
    }
  }

  // The pointer's height over the yard, as an aim down the rival's figure.
  // The drift is on top of the hand, so the tip is not where the finger is:
  // that is the sway to be held against.
  handFrom(event) {
    const rect = this.yardTarget.getBoundingClientRect()
    if (!rect.height) return this.hand
    const y = ((event.clientY - rect.top) / rect.height) * YARD.h
    return Math.min(Math.max((y - RIVAL.y - RIVAL_CROWN) / AIM_SPAN, 0), 1)
  }

  keyDown(event) {
    if (!this.pass) return
    switch (event.key) {
    case "ArrowUp": case "w": case "W": this.keys.add("up"); event.preventDefault(); break
    case "ArrowDown": case "s": case "S": this.keys.add("down"); event.preventDefault(); break
    case " ": case "Enter": event.preventDefault(); this.strike(); break
    }
  }

  keyUp(event) {
    switch (event.key) {
    case "ArrowUp": case "w": case "W": this.keys.delete("up"); break
    case "ArrowDown": case "s": case "S": this.keys.delete("down"); break
    }
  }

  // ------------------------------------------------------------------ words

  say(text) {
    this.callTarget.textContent = text
  }

  tally(ridden) {
    const rival = this.course?.rival?.name || "the rival"
    this.tallyTarget.textContent = `Pass ${Math.min(ridden + 1, this.valueOfPasses())} of ${this.valueOfPasses()} · you ${this.mine} · ${rival} ${this.theirs}`
  }

  flash(word) {
    this.flashTarget.textContent = word
    this.flashTarget.setAttribute("visibility", "visible")
    this.flashTarget.classList.remove("ll-joust__flash--in")
    void this.flashTarget.getBoundingClientRect()
    this.flashTarget.classList.add("ll-joust__flash--in")
  }

  showError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.hidden = !message
    this.errorTarget.textContent = message || ""
  }

  ordinal(n) {
    const s = [ "th", "st", "nd", "rd" ]
    const v = n % 100
    return n + (s[(v - 20) % 10] || s[v] || s[0])
  }

  headers() {
    return {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content
    }
  }
}
